import React, { useState, useEffect } from "react";
import MapPicker from "./MapPicker";
import AddressModal from "./AddressModal";
import { PencilIcon } from "lucide-react";

const ADDRESS_TYPES = ["Office", "WareHouse"];

const RegisterAddressStep = ({
  addresses,
  setAddresses,
  loadingAction,
  onFinish,
  onBack,
  isMapLoaded
}) => {
  // ─── Modal state ───────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null); // 0 = Office, 1 = Warehouse

  // ─── Address helpers ──────────────────────────────────────────────────────
  const updateAddress = (idx, field, val) => {
    const updated = [...addresses];
    updated[idx][field] = val;
    setAddresses(updated);
  };

  const updateAddressBulk = (idx, fields) => {
    const updated = [...addresses];
    updated[idx] = { ...updated[idx], ...fields };
    setAddresses(updated);
  };

  const handleMapAddressChange = (idx, addrObj) => {
    updateAddressBulk(idx, {
      addressLine1: addrObj.addressLine1 || "",
      city: addrObj.city || "",
      state: addrObj.state || "",
      pincode: addrObj.pincode || "",
      lat: addrObj.lat ?? null,
      lng: addrObj.lng ?? null,
    });
  };

  // ─── Normalize addresses to always have Office + optional Warehouse ───────
  useEffect(() => {
    let updated = [...addresses];

    // Ensure first address is always Office
    if (updated.length === 0) {
      updated.push({
        addressType: "Office",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
        lat: null,
        lng: null,
      });
    }
    if (updated[0].addressType !== "Office") {
      updated[0].addressType = "Office";
    }

    // Ensure max 2 addresses
    if (updated.length > 2) updated = updated.slice(0, 2);

    // If we have exactly 2, ensure second is Warehouse
    if (updated.length === 2 && updated[1].addressType !== "WareHouse") {
      updated[1].addressType = "WareHouse";
    }

    if (JSON.stringify(updated) !== JSON.stringify(addresses)) {
      setAddresses(updated);
    }
  }, [addresses, setAddresses]);

  // ─── Modal handlers ───────────────────────────────────────────────────────
  const openModal = (index) => {
    setEditingIndex(index);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingIndex(null);
  };

  const toggleSameAddress = () => {
    const updated = [...addresses];

    const office = updated[0];

    const isCurrentlySame =
      updated[1] &&
      office.addressLine1 === updated[1].addressLine1 &&
      office.city === updated[1].city &&
      office.pincode === updated[1].pincode;

    if (isCurrentlySame) {
      if (updated.length === 2) {
        updated[1] = {
          addressType: "WareHouse",
          addressLine1: "",
          addressLine2: "",
          city: "",
          state: "",
          pincode: "",
          country: "India",
          lat: null,
          lng: null,
        };
      }
    } else {

      if (updated.length === 1) {
        // create warehouse
        updated.push({
          ...office,
          addressType: "WareHouse",
        });
      } else {
        // overwrite warehouse
        updated[1] = {
          ...office,
          addressType: "WareHouse",
        };
      }
    }

    setAddresses(updated);
  };

  const isSameAddress = addresses.length === 2 &&
    addresses[0].addressLine1 === addresses[1].addressLine1 &&
    addresses[0].city === addresses[1].city &&
    addresses[0].pincode === addresses[1].pincode;

  // ─── Render single address card (Office or Warehouse) ─────────────────────
  const renderAddressCard = (index) => {
    const addr = addresses[index];
    const typeName = index === 0 ? "Office" : "Warehouse";
    const isFilled = addr && addr.addressLine1 && addr.city && addr.pincode;
    const bgColor = index === 0 ? "bg-[#F8FAFC]" : "bg-[#F0FDF4]";
    const iconBg = index === 0 ? "bg-red-500" : "bg-green-500";
    const iconEmoji = index === 0 ? "📍" : "📦";

    return (
      <div
        key={index}
        className="border-2 border-gray-200 rounded-2xl bg-white overflow-hidden shadow-sm"
      >
        {/* Card header - exactly like your mobile screenshot */}
        <div className={`flex items-center justify-between px-4 py-3 ${bgColor} border-b border-gray-200`}>
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0`}>
              <span className="text-white text-sm">{iconEmoji}</span>
            </div>
            <div>
              <div className="font-semibold text-gray-900">{typeName} Location</div>
              <div className="text-xs text-gray-500">
                {index === 0 ? "Primary Location" : "Secondary Location"}
              </div>
            </div>
          </div>

          {isFilled ? (
            <button
              onClick={() => openModal(index)}
              className="text-gray-400 hover:text-gray-600"
            >
              <PencilIcon size={18} />
            </button>
          ) : (
            <button
              onClick={() => openModal(index)}
              className="flex items-center gap-1 text-primaryColor font-medium"
            >
              <span className="text-2xl">+</span>
              <span className="text-sm">Add</span>
            </button>
          )}
        </div>

        {/* Filled content - exactly like second screenshot */}
        {isFilled && (
          <div className="p-4 text-sm text-gray-600 leading-relaxed">
            <p className="font-medium">{addr.addressLine1}</p>
            {addr.addressLine2 && <p>{addr.addressLine2}</p>}
            <p>
              {addr.city}, {addr.state} {addr.pincode}
            </p>
          </div>
        )}

        {/* Empty placeholder */}
        {!isFilled && (
          <div className="p-8 text-center text-gray-400 text-sm">
            Tap “+ Add” to set {typeName.toLowerCase()} location
          </div>
        )}
      </div>
    );
  };

  if (!isMapLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">

          {/* Animated loader */}
          <div className="w-12 h-12 border-4 border-primaryColor/30 border-t-primaryColor rounded-full animate-spin" />

          {/* Text */}
          <p className="text-sm text-gray-600 font-medium">
            Loading map experience...
          </p>

          {/* Optional subtext */}
          <p className="text-xs text-gray-400">
            Getting things ready for you
          </p>
        </div>
      </div>
    );
  }

  const isOfficeFilled =
    addresses[0]?.addressLine1 &&
    addresses[0]?.city &&
    addresses[0]?.pincode;

  return (
    <div className="space-y-5 sm:max-h-[full] pt-5 sm:py-5 overflow-y-auto pr-1 ">
      {/* Office Location Card - always first */}
      {renderAddressCard(0)}

      {/* Warehouse Location Card */}
      {addresses.length >= 2 ? (
        renderAddressCard(1)
      ) : (
        /* Dashed "Add Warehouse" card - exactly like first screenshot */
        <button
          type="button"
          onClick={() => {
            const newWarehouse = {
              addressType: "WareHouse",
              addressLine1: "",
              addressLine2: "",
              city: "",
              state: "",
              pincode: "",
              country: "India",
              lat: null,
              lng: null,
            };
            setAddresses([...addresses, newWarehouse]);
            // Open modal immediately for warehouse
            setTimeout(() => openModal(1), 10);
          }}
          className="w-full border-2 border-dashed border-gray-300 text-gray-500 hover:border-green-500 hover:text-green-600 hover:bg-green-50 py-8 rounded-2xl text-sm font-medium transition-all duration-200 flex flex-col items-center justify-center gap-1"
        >
          <span className="text-4xl mb-1">+</span>
          Warehouse Location
        </button>
      )}

      {/* If Both Are Same Address checkbox - shown only when 2 addresses exist */}
      {isOfficeFilled && (
        <label className="flex items-center gap-2 px-1 py-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isSameAddress}
            onChange={toggleSameAddress}
            className="w-5 h-5 accent-green-600 rounded border-gray-300"
          />
          <span className="text-sm font-medium text-gray-700">
            If Both Are Same Address
          </span>
        </label>
      )}

      {/* ── Action buttons ───────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row gap-3 mt-6 pb-4">
        <button
          type="button"
          onClick={onBack}
          className="
            flex-1 border-2 border-gray-200 text-gray-600 py-3.5 rounded-xl
            font-medium text-sm hover:border-primaryColor hover:text-primaryColor
            transition-all duration-200 active:scale-[0.98]
            flex items-center justify-center gap-2
          "
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5 5-5M18 12H6" />
          </svg>
          Back
        </button>

        <button
          onClick={onFinish}
          disabled={loadingAction === "register"}
          className="
            flex-1 bg-primaryColor text-white py-3.5 rounded-xl
            font-semibold text-sm hover:bg-primaryColor/90
            transition-all duration-200 active:scale-[0.98]
            disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100
            flex items-center justify-center gap-2 shadow-md shadow-primaryColor/30
          "
        >
          {loadingAction === "register" ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Saving...
            </>
          ) : (
            `${addresses.length ? "Finish Registration" : "Skip & Finish"}`
          )}
        </button>
      </div>

      {/* ── MODAL (Bottom sheet on mobile, centered on desktop) ──────────────── */}
      <AddressModal
        showModal={showModal}
        closeModal={closeModal}
        editingIndex={editingIndex}
        addresses={addresses}
        setAddresses={setAddresses}
        handleMapAddressChange={handleMapAddressChange}
        updateAddress={updateAddress}
      />
    </div>
  );
};

export default RegisterAddressStep;